/**
 * SlackConfigCard - Slack notification configuration card
 *
 * Features:
 * - Display Slack webhook configuration
 * - Add/edit webhook URL
 * - Remove configuration
 * - Mask sensitive webhook URL
 */

import { createPortal } from 'react-dom';
import { Edit2, Trash2, X, Loader2 } from 'lucide-react';
import { StatusBadge, Card, ConfirmDialog } from '../common';
import { SlackLogo } from '../icons';
import type { SlackConfigResponse } from '@resolve-ai/shared';

interface SlackConfigCardProps {
  slackConfig: SlackConfigResponse | null;
  isAdmin: boolean;
  showModal: boolean;
  showDeleteConfirm: boolean;
  slackWebhookUrl: string;
  isSaving: boolean;
  hasChanges: boolean;
  onOpenModal: () => void;
  onCloseModal: () => void;
  onSetSlackWebhookUrl: (url: string) => void;
  onSave: () => void;
  onDelete: () => Promise<void>;
  onSetShowDeleteConfirm: (show: boolean) => void;
}

// Helper function to mask webhook URL
const maskWebhookUrl = (url: string): string => {
  try {
    const parsed = new URL(url);
    return `${parsed.host}/services/${'•'.repeat(6)}/${'•'.repeat(6)}`;
  } catch {
    return `${'•'.repeat(20)}`;
  }
};

export function SlackConfigCard({
  slackConfig,
  isAdmin,
  showModal,
  showDeleteConfirm,
  slackWebhookUrl,
  isSaving,
  hasChanges,
  onOpenModal,
  onCloseModal,
  onSetSlackWebhookUrl,
  onSave,
  onDelete,
  onSetShowDeleteConfirm,
}: SlackConfigCardProps) {
  return (
    <>
      <Card>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <SlackLogo className="w-4 h-4" />
            <h3 className="text-[1rem] leading-[140%] font-semibold text-gray-900 dark:text-white">
              Slack Notifications
            </h3>
          </div>
          <div className="flex items-center gap-2">
            <StatusBadge status={slackConfig ? 'connected' : 'not_connected'} size="lg" />
            {isAdmin && (
              <>
                {slackConfig && (
                  <>
                    <button
                      onClick={onOpenModal}
                      className="p-2 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                      title="Edit Slack configuration"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => onSetShowDeleteConfirm(true)}
                      className="p-2 dark:text-gray-400 hover:text-red-600 hover:dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                      title="Remove Slack configuration"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </>
                )}
                {!slackConfig && (
                  <button
                    onClick={onOpenModal}
                    className="px-3 py-1  text-sm bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors"
                  >
                    Configure
                  </button>
                )}
              </>
            )}
          </div>
        </div>
        {!slackConfig ? (
          <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-600">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Configure a webhook to receive notifications about bug fixes and updates
            </p>
          </div>
        ) : (
          <div>
            {/* Masked Webhook URL */}
            <p className="font-caption text-gray-500 dark:text-white-500 capitalize  mb-[0.38rem]">Webhook</p>
            <p className="font-body-base text-gray-900 dark:text-white">
              {maskWebhookUrl(slackConfig?.webhookUrl)}
            </p>
          </div>
        )}
      </Card>

      {/* Slack Configuration Modal */}
      {showModal && createPortal(
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl max-w-lg w-full">
            <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                {slackConfig ? 'Edit Slack Configuration' : 'Configure Slack Notifications'}
              </h3>
              <button
                onClick={onCloseModal}
                className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4">
              <div>
                <label
                  htmlFor="slackWebhookUrl"
                  className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                >
                  Slack Webhook URL
                </label>
                <input
                  type="url"
                  id="slackWebhookUrl"
                  value={slackWebhookUrl}
                  onChange={(e) => onSetSlackWebhookUrl(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  placeholder="https://hooks.slack.com/services/..."
                  autoFocus
                />
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Get your webhook URL from Slack&apos;s Incoming Webhooks app
                </p>
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 p-4 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={onCloseModal}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                disabled={isSaving}
              >
                Cancel
              </button>
              <button
                onClick={onSave}
                disabled={!slackWebhookUrl.trim() || isSaving || !hasChanges}
                className={`px-4 py-2 rounded-lg transition-colors flex items-center ${slackWebhookUrl.trim() && !isSaving && hasChanges
                  ? 'bg-indigo-600 hover:bg-indigo-700 text-white'
                  : 'bg-gray-300 dark:bg-gray-600 text-gray-500 cursor-not-allowed'
                  }`}
              >
                {isSaving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {isSaving ? 'Saving...' : 'Save Configuration'}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Delete Slack Confirmation */}
      <ConfirmDialog
        isOpen={showDeleteConfirm}
        title="Remove Slack Configuration"
        message="Are you sure you want to remove Slack notifications from this project? This action cannot be undone."
        confirmLabel="Remove"
        variant="danger"
        onConfirm={onDelete}
        onClose={() => onSetShowDeleteConfirm(false)}
      />
    </>
  );
}
